<template>
  <div class="overflow-hidden">
    <!-- Header with AI Tools -->
    <div class="border-b border-gray-200">
      <div class="px-3 py-1.5 flex items-center justify-between bg-white">
        <!-- AI Tools -->
        <div class="flex items-center space-x-1">
          <button
            @click="resetToTemplate"
            :disabled="isContentEmpty"
            class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            title="Vider le message (garder bonjour/signature)"
          >
            <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Vider
          </button>

          <button
            @click="generateSuggestion"
            :disabled="loadingSuggest || loadingImprove || loadingTranslate"
            class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="!loadingSuggest" class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <svg v-else class="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            AI
          </button>

          <button
            v-if="!isContentEmpty"
            @click="improveResponse"
            :disabled="loadingSuggest || loadingImprove || loadingTranslate"
            class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="!loadingImprove" class="h-3 w-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <svg v-else class="animate-spin h-3 w-3 mr-0.5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Améliorer
          </button>

          <button
            v-if="!isContentEmpty"
            @click="translateResponse"
            :disabled="loadingSuggest || loadingImprove || loadingTranslate"
            class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg v-if="!loadingTranslate" class="h-3 w-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
            </svg>
            <svg v-else class="animate-spin h-3 w-3 mr-0.5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traduire
          </button>
        </div>

        <button
          @click="togglePreview"
          class="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
        >
          <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {{ showPreview ? 'Éditer' : 'Aperçu' }}
        </button>
      </div>

      <!-- AI Info Banner -->
      <div v-if="aiInfo" class="px-3 py-1.5 bg-gray-50 border-t border-gray-200">
        <div class="flex items-start">
          <svg class="h-3 w-3 text-gray-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <div class="ml-2 flex-1">
            <p class="text-xs text-gray-700 font-medium">{{ aiInfo }}</p>
            <div v-if="aiDetails" class="mt-1 space-y-0.5">
              <div v-if="aiDetails.confidence" class="text-xs text-gray-600">
                Confiance: {{ aiDetails.confidence }}%
              </div>
              <div v-if="aiDetails.detected_language" class="text-xs text-gray-600">
                Langue détectée: {{ aiDetails.detected_language }}
              </div>
              <div v-if="aiDetails.target_language" class="text-xs text-gray-600">
                Langue cible: {{ aiDetails.target_language }}
              </div>
              <div v-if="aiDetails.changes && aiDetails.changes.length > 0" class="mt-2">
                <p class="text-xs font-medium text-gray-700 mb-1">Modifications :</p>
                <ul class="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                  <li v-for="(change, index) in aiDetails.changes" :key="index">{{ change }}</li>
                </ul>
              </div>
            </div>
          </div>
          <button @click="aiInfo = null; aiDetails = null" class="ml-2 flex-shrink-0">
            <svg class="h-3 w-3 text-gray-400 hover:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="px-3 py-1 border-b border-gray-200 bg-white">
      <div class="flex items-center space-x-1">
        <button
          @click="insertMarkdown('**', '**')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
          title="Gras"
        >
          <strong>B</strong>
        </button>
        <button
          @click="insertMarkdown('*', '*')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 italic"
          title="Italique"
        >
          <em>I</em>
        </button>
        <button
          @click="insertMarkdown('## ', '')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
          title="Titre"
        >
          H
        </button>
        <button
          @click="insertMarkdown('- ', '')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
          title="Liste"
        >
          •
        </button>
        <button
          @click="insertMarkdown('`', '`')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50 font-mono"
          title="Code"
        >
          &lt;&gt;
        </button>
        <button
          @click="insertMarkdown('[', '](url)')"
          class="px-1.5 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
          title="Lien"
        >
          L
        </button>
      </div>
    </div>

    <!-- Editor/Preview Area -->
    <div class="p-3">
      <textarea
        v-if="!showPreview"
        ref="textarea"
        v-model="content"
        @input="updateContent"
        placeholder="Écrivez votre réponse en markdown ici...&#10;&#10;**Gras**, *italique*, ## Titres, - Listes, etc."
        class="w-full min-h-[250px] border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm resize-y"
      ></textarea>
      <div
        v-else
        class="min-h-[250px] border border-gray-300 rounded-md p-3 bg-gray-50"
      >
        <Markdown
          :source="content"
          class="prose prose-sm max-w-none"
        />
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="px-3 py-1.5 bg-white border-t border-gray-200 flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <!-- Auto-save indicator -->
        <div v-if="autoSaveStatus" class="text-xs text-gray-400 flex items-center">
          <svg v-if="autoSaveStatus === 'saving'" class="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="h-3 w-3 mr-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          {{ autoSaveStatus === 'saving' ? 'Enregistrement...' : 'Enregistré' }}
        </div>
        <div class="h-3 w-px bg-gray-300"></div>
        <label class="inline-flex items-center">
          <input
            type="checkbox"
            v-model="changeStatus"
            class="rounded border-gray-300 text-gray-900 shadow-sm focus:border-gray-400 focus:ring focus:ring-gray-200 focus:ring-opacity-50 h-3 w-3"
          >
          <span class="ml-1.5 text-xs text-gray-600">Statut</span>
        </label>
        <select
          v-if="changeStatus"
          v-model="newStatus"
          class="block rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-xs py-0.5 pl-2 pr-7"
        >
          <option value="Answered">Répondu</option>
          <option value="On Hold">En attente</option>
          <option value="In Progress">En cours</option>
          <option value="Closed">Fermé</option>
        </select>
      </div>

      <div class="flex items-center space-x-1">
        <button
          @click="openKBModal"
          :disabled="isContentEmpty"
          type="button"
          class="inline-flex items-center px-2 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          :title="kbSourceId ? 'Mettre à jour dans la KB' : 'Sauvegarder dans la KB'"
        >
          <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
          KB
        </button>
        <button
          @click="submitResponse"
          :disabled="isContentEmpty || submitting"
          type="button"
          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-gray-900 hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="!submitting" class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          <svg v-else class="animate-spin h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ submitting ? 'Envoi...' : 'Envoyer' }}
        </button>
      </div>
    </div>

    <!-- Modal KB -->
    <div v-if="showKBModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeKBModal"></div>

        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <h3 class="text-base font-medium text-gray-900" id="modal-title">
                {{ kbSourceId ? 'Mettre à jour la réponse KB' : 'Sauvegarder dans la Knowledge Base' }}
              </h3>
              <p class="mt-1 text-xs text-gray-500">
                {{ kbSourceId ? 'Modifier la réponse existante dans la base de connaissances' : 'Créer une nouvelle réponse dans la base de connaissances' }}
              </p>
            </div>

            <!-- Error message -->
            <div v-if="kbError" class="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              {{ kbError }}
            </div>

            <div class="space-y-3">
              <!-- Title -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Titre <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="kbForm.title"
                  type="text"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                  placeholder="Ex: Installation Docker sur VPS"
                />
              </div>

              <!-- Content Preview -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Aperçu du contenu
                </label>
                <div class="max-h-32 overflow-y-auto border border-gray-300 rounded p-2 bg-gray-50 text-xs">
                  {{ kbForm.content.substring(0, 200) }}{{ kbForm.content.length > 200 ? '...' : '' }}
                </div>
                <p class="mt-1 text-xs text-gray-500">
                  {{ kbForm.content.length }} caractères
                </p>
              </div>

              <!-- Department -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Département
                </label>
                <select
                  v-model="kbForm.deptname"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                >
                  <option value="">-- Aucun --</option>
                  <option v-for="dept in departments" :key="dept" :value="dept">
                    {{ dept }}
                  </option>
                </select>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-xs font-medium text-gray-700 mb-1">
                  Priorité
                </label>
                <input
                  v-model.number="kbForm.priority"
                  type="number"
                  min="0"
                  class="block w-full rounded border-gray-300 shadow-sm focus:border-gray-400 focus:ring-gray-200 text-sm"
                  placeholder="0"
                />
                <p class="mt-1 text-xs text-gray-500">
                  Plus élevé = plus prioritaire (défaut: 0)
                </p>
              </div>

              <!-- Active -->
              <div>
                <label class="inline-flex items-center">
                  <input
                    v-model="kbForm.is_active"
                    type="checkbox"
                    class="rounded border-gray-300 text-gray-900 shadow-sm focus:border-gray-400 focus:ring focus:ring-gray-200 focus:ring-opacity-50"
                  >
                  <span class="ml-2 text-xs text-gray-700">Réponse active</span>
                </label>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="saveToKB"
              :disabled="savingToKB"
              type="button"
              class="w-full inline-flex justify-center rounded border border-transparent shadow-sm px-4 py-2 bg-gray-900 text-xs font-medium text-white hover:bg-gray-800 focus:outline-none sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="savingToKB" class="animate-spin h-3 w-3 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ savingToKB ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
            <button
              @click="closeKBModal"
              :disabled="savingToKB"
              type="button"
              class="mt-3 w-full inline-flex justify-center rounded border border-gray-300 shadow-sm px-4 py-2 bg-white text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import Markdown from 'vue3-markdown-it';

export default {
  name: 'RichMarkdownEditor',
  components: {
    Markdown
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    ticketId: {
      type: String,
      required: true
    },
    supportService: {
      type: Object,
      required: true
    },
    defaultTemplate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      content: this.modelValue || '',
      changeStatus: true,
      newStatus: 'Answered',
      submitting: false,
      aiInfo: null,
      aiDetails: null,
      loadingSuggest: false,
      loadingImprove: false,
      loadingTranslate: false,
      showPreview: false,
      autoSaveStatus: null, // 'saving' | 'saved' | null
      autoSaveTimeout: null,
      showKBModal: false,
      savingToKB: false,
      kbSourceId: null, // ID de la réponse KB source si elle provient de la KB
      kbSourceData: null, // Données de la réponse KB source pour pré-remplir le formulaire
      kbForm: {
        deptname: '',
        title: '',
        content: '',
        priority: 0,
        is_active: true
      },
      kbError: null,
      departments: []
    };
  },
  async mounted() {
    // Configurer marked pour interpréter les sauts de ligne correctement
    marked.setOptions({
      gfm: true,        // GitHub Flavored Markdown
    });

    // Restaurer le brouillon depuis localStorage si disponible
    this.restoreDraft();

    // Charger la liste des départements
    await this.loadDepartments();
  },
  beforeUnmount() {
    // Nettoyer le timeout en cours
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
  },
  computed: {
    isContentEmpty() {
      return !this.content || this.content.trim() === '';
    },
    draftKey() {
      return `ticket_draft_${this.ticketId}`;
    }
  },
  watch: {
    modelValue(newValue) {
      if (newValue !== this.content) {
        this.content = newValue || '';
      }
    },
    content(newValue) {
      // Auto-save avec un délai de 1 seconde
      this.debouncedSaveDraft();
    }
  },
  methods: {
    updateContent() {
      this.$emit('update:modelValue', this.content);
    },

    togglePreview() {
      this.showPreview = !this.showPreview;
    },

    // Auto-save methods
    restoreDraft() {
      try {
        const draft = localStorage.getItem(this.draftKey);
        if (draft && draft.trim()) {
          // Ne restaurer que si l'éditeur est vide ou contient le message par défaut
          if (!this.content || this.content.trim() === '' || this.content === this.modelValue) {
            this.content = draft;
            this.$emit('update:modelValue', draft);
            console.log('Brouillon restauré depuis localStorage');
          }
        }
      } catch (error) {
        console.error('Error restoring draft:', error);
      }
    },

    debouncedSaveDraft() {
      // Annuler le timeout précédent
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout);
      }

      // Afficher l'indicateur "Sauvegarde..."
      this.autoSaveStatus = 'saving';

      // Sauvegarder après 1 seconde d'inactivité
      this.autoSaveTimeout = setTimeout(() => {
        this.saveDraft();
      }, 1000);
    },

    saveDraft() {
      try {
        if (this.content && this.content.trim()) {
          localStorage.setItem(this.draftKey, this.content);
          this.autoSaveStatus = 'saved';

          // Masquer l'indicateur après 2 secondes
          setTimeout(() => {
            this.autoSaveStatus = null;
          }, 2000);
        }
      } catch (error) {
        console.error('Error saving draft:', error);
        this.autoSaveStatus = null;
      }
    },

    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey);
        this.autoSaveStatus = null;
        console.log('Brouillon supprimé du localStorage');
      } catch (error) {
        console.error('Error clearing draft:', error);
      }
    },

    insertMarkdown(before, after) {
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = this.content.substring(start, end);

      const newText = this.content.substring(0, start) +
                     before + selectedText + after +
                     this.content.substring(end);

      this.content = newText;
      this.$emit('update:modelValue', this.content);

      // Repositionner le curseur
      this.$nextTick(() => {
        textarea.focus();
        const newCursorPos = start + before.length + selectedText.length;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      });
    },

    // Méthode publique pour insérer du contenu markdown
    setMarkdownContent(markdown) {
      if (!markdown) return;
      this.content = markdown;
      this.$emit('update:modelValue', markdown);
    },

    // Méthode publique pour insérer du markdown à la position actuelle
    insertMarkdownContent(markdown, kbId = null) {
      if (!markdown) return;
      if (this.content.trim()) {
        this.content += '\n\n' + markdown;
      } else {
        this.content = markdown;
      }
      this.$emit('update:modelValue', this.content);

      // Si le contenu provient de la KB, sauvegarder l'ID source
      if (kbId) {
        this.kbSourceId = kbId;
      }
    },

    // Focus sur l'éditeur
    focus() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea;
        if (textarea) {
          textarea.focus();
        }
      });
    },

    async generateSuggestion() {
      this.loadingSuggest = true;
      this.aiInfo = null;
      this.aiDetails = null;

      try {
        const response = await this.supportService.getSuggestedResponse(this.ticketId, true);

        if (response.success) {
          this.content = response.suggestion;
          this.$emit('update:modelValue', response.suggestion);

          this.aiInfo = 'Réponse suggérée par l\'IA générée avec succès';
          this.aiDetails = {
            confidence: response.confidence,
            changes: response.changes || null
          };
        } else {
          this.aiInfo = (response.message || 'Erreur lors de la génération');
        }
      } catch (error) {
        console.error('Error generating suggestion:', error);
        this.aiInfo = (error.response?.data?.message || 'Erreur lors de la génération');
      } finally {
        this.loadingSuggest = false;
      }
    },

    async improveResponse() {
      if (this.isContentEmpty) return;

      this.loadingImprove = true;
      this.aiInfo = null;
      this.aiDetails = null;

      try {
        const response = await this.supportService.improveResponse(this.ticketId, this.content);

        if (response.success) {
          this.content = response.improved_response;
          this.$emit('update:modelValue', response.improved_response);
          this.aiInfo = '✨ Réponse améliorée avec succès !';
          this.aiDetails = {
            changes: response.changes || null
          };
        } else {
          this.aiInfo = '❌ ' + (response.message || 'Erreur');
        }
      } catch (error) {
        console.error('Error improving response:', error);
        this.aiInfo = '❌ ' + (error.response?.data?.message || 'Erreur');
      } finally {
        this.loadingImprove = false;
      }
    },

    async translateResponse() {
      if (this.isContentEmpty) return;

      this.loadingTranslate = true;
      this.aiInfo = null;
      this.aiDetails = null;

      try {
        // Envoyer tout le contenu de l'éditeur, incluant les formules de politesse et la signature
        const response = await this.supportService.translateAndImprove(this.ticketId, this.content);

        if (response.success) {
          this.content = response.translated_message;
          this.$emit('update:modelValue', response.translated_message);
          this.aiInfo = '🌐 Traduction automatique appliquée avec succès !';
          this.aiDetails = {
            detected_language: response.detected_language,
            target_language: response.target_language,
            changes: response.changes || null
          };
        } else {
          this.aiInfo = '❌ ' + (response.message || 'Erreur');
        }
      } catch (error) {
        console.error('Error translating:', error);
        this.aiInfo = '❌ ' + (error.response?.data?.message || 'Erreur');
      } finally {
        this.loadingTranslate = false;
      }
    },

    async submitResponse() {
      if (this.isContentEmpty) return;

      this.submitting = true;

      try {
        const data = {
          message: this.content,
          markdown: true
        };

        if (this.changeStatus) {
          data.status = this.newStatus;
        }

        const response = await this.supportService.replyToTicket(this.ticketId, data);

        if (response.success) {
          // Nettoyer le brouillon après l'envoi réussi
          this.clearDraft();
          this.$emit('success', response);
        } else {
          this.$emit('error', response.message || 'Erreur lors de l\'envoi');
        }
      } catch (error) {
        console.error('Error submitting:', error);
        this.$emit('error', error.response?.data?.message || 'Erreur lors de l\'envoi');
      } finally {
        this.submitting = false;
      }
    },

    async openKBModal() {
      this.kbError = null;

      // Si c'est une modification, charger les données de la réponse existante
      if (this.kbSourceId) {
        try {
          const response = await this.supportService.getKBResponse(this.kbSourceId);
          if (response.success) {
            this.kbForm = {
              deptname: response.response.deptname || '',
              title: response.response.title,
              content: response.response.content,
              priority: response.response.priority,
              is_active: response.response.is_active
            };
            this.kbSourceData = response.response;
          }
        } catch (error) {
          console.error('Error loading KB response:', error);
          this.kbError = 'Erreur lors du chargement de la réponse KB';
        }
      } else {
        // Nouvelle réponse - initialiser avec le contenu actuel de l'éditeur
        this.kbForm = {
          deptname: '',
          title: '',
          content: this.content,
          priority: 0,
          is_active: true
        };
        this.kbSourceData = null;
      }

      this.showKBModal = true;
    },

    closeKBModal() {
      this.showKBModal = false;
      this.kbError = null;
    },

    async saveToKB() {
      if (!this.kbForm.title.trim()) {
        this.kbError = 'Le titre est requis';
        return;
      }

      if (!this.kbForm.content.trim()) {
        this.kbError = 'Le contenu ne peut pas être vide';
        return;
      }

      this.savingToKB = true;
      this.kbError = null;

      try {
        const payload = {
          deptname: this.kbForm.deptname || null,
          title: this.kbForm.title,
          content: this.kbForm.content,
          priority: this.kbForm.priority,
          is_active: this.kbForm.is_active
        };

        let response;
        if (this.kbSourceId) {
          // Mise à jour d'une réponse existante
          response = await this.supportService.updateKBResponse(this.kbSourceId, payload);
        } else {
          // Création d'une nouvelle réponse
          response = await this.supportService.createKBResponse(payload);
        }

        if (response.success) {
          this.$emit('kb-saved', response);
          this.closeKBModal();

          // Réinitialiser l'ID source après sauvegarde d'une nouvelle réponse
          if (!this.kbSourceId && response.id) {
            this.kbSourceId = response.id;
          }

          // Afficher un message de succès
          this.aiInfo = this.kbSourceId
            ? 'Réponse KB mise à jour avec succès'
            : 'Nouvelle réponse ajoutée à la KB';
          setTimeout(() => {
            this.aiInfo = null;
          }, 3000);
        } else {
          this.kbError = response.message || 'Erreur lors de la sauvegarde';
        }
      } catch (error) {
        console.error('Error saving to KB:', error);
        if (error.response?.data?.errors) {
          // Erreur de validation
          const errors = error.response.data.errors;
          const errorMessages = Object.values(errors).flat();
          this.kbError = errorMessages.join(', ');
        } else {
          this.kbError = error.response?.data?.message || 'Erreur lors de la sauvegarde';
        }
      } finally {
        this.savingToKB = false;
      }
    },

    async loadDepartments() {
      try {
        const response = await this.supportService.getKBDepartments();
        if (response.success) {
          this.departments = response.departments || [];
        }
      } catch (error) {
        console.error('Error loading departments:', error);
      }
    },

    resetToTemplate() {
      // Réinitialiser le contenu avec le template par défaut (bonjour + signature)
      if (this.defaultTemplate) {
        this.content = this.defaultTemplate;
        this.$emit('update:modelValue', this.defaultTemplate);
        this.aiInfo = 'Message réinitialisé';
        setTimeout(() => {
          this.aiInfo = null;
        }, 2000);
      }
    }
  }
};
</script>

<style scoped>
/* Styles pour le mode aperçu */
.prose :deep(h1) { @apply text-xl font-bold mb-3 mt-6; }
.prose :deep(h2) { @apply text-lg font-bold mb-3 mt-5; }
.prose :deep(h3) { @apply text-base font-bold mb-2 mt-4; }
.prose :deep(p) { @apply mb-4; }
.prose :deep(ul) { @apply list-disc pl-5 mb-4 space-y-1; }
.prose :deep(ol) { @apply list-decimal pl-5 mb-4 space-y-1; }
.prose :deep(li) { @apply mb-1; }
.prose :deep(code) { @apply bg-gray-100 px-2 py-1 rounded text-sm font-mono; }
.prose :deep(pre) { @apply bg-gray-900 text-gray-100 p-4 rounded text-sm overflow-x-auto mb-4; }
.prose :deep(pre code) { @apply bg-transparent text-gray-100 p-0; }
.prose :deep(strong) { @apply font-bold; }
.prose :deep(em) { @apply italic; }
.prose :deep(a) { @apply text-indigo-600 hover:text-indigo-800 underline; }
.prose :deep(blockquote) { @apply border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4; }
.prose :deep(hr) { @apply border-t-2 border-gray-200 my-6; }
</style>
